'use client'

import { useEffect, useRef } from 'react'
import { usePalette } from '@/contexts/PaletteContext'

interface RothkoShaderProps {
  className?: string
}

// Vertex shader - simple fullscreen quad
const vertexShaderSource = `
  attribute vec2 a_position;
  varying vec2 v_uv;

  void main() {
    v_uv = a_position * 0.5 + 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`

// Fragment shader - Rothko-inspired color fields
const fragmentShaderSource = `
  precision highp float;

  varying vec2 v_uv;

  uniform float u_time;
  uniform vec2 u_resolution;
  uniform vec3 u_groundColor;
  uniform vec3 u_topColor1;
  uniform vec3 u_topColor2;
  uniform vec3 u_topColor3;
  uniform vec3 u_bottomColor1;
  uniform vec3 u_bottomColor2;
  uniform vec3 u_hazeColor;

  // Simplex noise functions
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }

  float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439,
                        -0.577350269189626, 0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy));
    vec2 x0 = v -   i + dot(i, C.xx);
    vec2 i1;
    i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod289(i);
    vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0))
                            + i.x + vec3(0.0, i1.x, 1.0));
    vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy),
                            dot(x12.zw,x12.zw)), 0.0);
    m = m*m;
    m = m*m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);
    vec3 g;
    g.x  = a0.x  * x0.x  + h.x  * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
  }

  // Fractal Brownian Motion for organic texture
  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.5;
    float frequency = 1.0;
    for (int i = 0; i < 4; i++) {
      value += amplitude * snoise(p * frequency);
      amplitude *= 0.5;
      frequency *= 2.0;
    }
    return value;
  }

  // Soft rectangle with organic edges
  float softRect(vec2 uv, float top, float bottom, float softness, float time) {
    // Add organic wobble to edges (subtle, not spiky)
    float noiseScale = 2.5;
    float edgeNoise = fbm(vec2(uv.x * noiseScale, time * 0.1)) * 0.015;
    float edgeNoise2 = fbm(vec2(uv.x * noiseScale * 1.5 + 100.0, time * 0.08)) * 0.008;

    float topEdge = top + edgeNoise + edgeNoise2;
    float bottomEdge = bottom + edgeNoise - edgeNoise2;

    // Soft edges using smoothstep
    float shape = smoothstep(bottomEdge - softness, bottomEdge + softness * 0.5, uv.y);
    shape *= smoothstep(topEdge + softness, topEdge - softness * 0.5, uv.y);

    return shape;
  }

  // Inner glow effect
  float innerGlow(vec2 uv, float top, float bottom, float intensity) {
    float center = (top + bottom) * 0.5;
    float dist = abs(uv.y - center) / ((top - bottom) * 0.5);
    return (1.0 - dist * dist) * intensity;
  }

  void main() {
    vec2 uv = v_uv;
    float aspect = u_resolution.x / u_resolution.y;

    // Slow, breathing time
    float breathe = sin(u_time * 0.3) * 0.5 + 0.5;
    float breathe2 = sin(u_time * 0.2 + 1.0) * 0.5 + 0.5;

    // Start with ground color
    vec3 color = u_groundColor;

    // === TOP FIELD (upper 75% of screen) ===
    float topFieldTop = 1.05;
    float topFieldBottom = 0.32 + breathe * 0.02;
    float topShape = softRect(uv, topFieldTop, topFieldBottom, 0.08, u_time);

    // Blend between top colors based on vertical position and noise
    float topBlend = (uv.y - topFieldBottom) / (topFieldTop - topFieldBottom);
    topBlend = clamp(topBlend, 0.0, 1.0);
    float topNoise = fbm(uv * 2.0 + u_time * 0.05) * 0.3 + 0.5;

    vec3 topColor = mix(u_topColor1, u_topColor2, topBlend * topNoise);
    topColor = mix(topColor, u_topColor3, (1.0 - topBlend) * topNoise * 0.5);

    // Add inner luminosity
    float topGlow = innerGlow(uv, topFieldTop, topFieldBottom, 0.4 + breathe * 0.1);
    topColor += topGlow * u_topColor3 * 0.3;

    color = mix(color, topColor, topShape);

    // === BOTTOM FIELD (lower 35% of screen) ===
    float bottomFieldTop = 0.38 - breathe2 * 0.02;
    float bottomFieldBottom = -0.05;
    float bottomShape = softRect(uv, bottomFieldTop, bottomFieldBottom, 0.06, u_time + 50.0);

    // Blend bottom colors
    float bottomBlend = (uv.y - bottomFieldBottom) / (bottomFieldTop - bottomFieldBottom);
    bottomBlend = clamp(bottomBlend, 0.0, 1.0);
    float bottomNoise = fbm(uv * 2.5 + u_time * 0.03 + 100.0) * 0.4 + 0.5;

    vec3 bottomColor = mix(u_bottomColor2, u_bottomColor1, bottomBlend * bottomNoise);

    // Inner glow for bottom
    float bottomGlow = innerGlow(uv, bottomFieldTop, bottomFieldBottom, 0.3 + breathe2 * 0.1);
    bottomColor += bottomGlow * u_bottomColor1 * 0.2;

    color = mix(color, bottomColor, bottomShape);

    // === HAZE LAYER (where fields meet) ===
    float hazeCenter = 0.35 + breathe * 0.01;
    float hazeDist = abs(uv.y - hazeCenter);
    float hazeNoise = fbm(vec2(uv.x * 4.0, u_time * 0.15)) * 0.03;
    float haze = smoothstep(0.12 + hazeNoise, 0.0, hazeDist);
    haze *= 0.4 + breathe * 0.1;

    // Haze color blends the two fields
    vec3 hazeBlend = mix(u_bottomColor1, u_topColor1, 0.5);
    hazeBlend = mix(hazeBlend, u_hazeColor, 0.3);
    color = mix(color, hazeBlend, haze * 0.6);

    // === ATMOSPHERIC DEPTH ===
    // Add subtle color variation across the canvas
    float atmosphereNoise = fbm(uv * 1.5 + u_time * 0.02) * 0.08;
    color += atmosphereNoise * u_hazeColor * 0.15;

    // === GRAIN TEXTURE ===
    float grain = (fract(sin(dot(uv * u_resolution, vec2(12.9898, 78.233))) * 43758.5453) - 0.5) * 0.04;
    color += grain;

    // === VIGNETTE ===
    float vignette = 1.0 - length((uv - 0.5) * vec2(1.0, 0.6)) * 0.5;
    color *= vignette;

    // Ensure we don't clip
    color = clamp(color, 0.0, 1.0);

    gl_FragColor = vec4(color, 1.0);
  }
`

function hexToRgb(hex: string): [number, number, number] {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result) {
    return [
      parseInt(result[1], 16) / 255,
      parseInt(result[2], 16) / 255,
      parseInt(result[3], 16) / 255,
    ]
  }
  return [0, 0, 0]
}

export function RothkoShader({ className = '' }: RothkoShaderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { currentPalette: palette } = usePalette()
  const animationRef = useRef<number | null>(null)
  const glRef = useRef<WebGLRenderingContext | null>(null)
  const programRef = useRef<WebGLProgram | null>(null)
  const startTimeRef = useRef<number>(Date.now())

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Get WebGL context
    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      powerPreference: 'low-power',
    })

    if (!gl) {
      console.warn('WebGL not supported, falling back to CSS')
      return
    }

    glRef.current = gl

    // Compile shaders
    const vertexShader = gl.createShader(gl.VERTEX_SHADER)!
    gl.shaderSource(vertexShader, vertexShaderSource)
    gl.compileShader(vertexShader)

    if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
      console.error('Vertex shader error:', gl.getShaderInfoLog(vertexShader))
      return
    }

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!
    gl.shaderSource(fragmentShader, fragmentShaderSource)
    gl.compileShader(fragmentShader)

    if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
      console.error('Fragment shader error:', gl.getShaderInfoLog(fragmentShader))
      return
    }

    // Create program
    const program = gl.createProgram()!
    gl.attachShader(program, vertexShader)
    gl.attachShader(program, fragmentShader)
    gl.linkProgram(program)

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program))
      return
    }

    programRef.current = program
    gl.useProgram(program)

    // Set up geometry (fullscreen quad)
    const positions = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
       1,  1,
    ])

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW)

    const positionLocation = gl.getAttribLocation(program, 'a_position')
    gl.enableVertexAttribArray(positionLocation)
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

    // Handle resize
    const handleResize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const rect = canvas.getBoundingClientRect()
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      gl.viewport(0, 0, canvas.width, canvas.height)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  // Update colors when palette changes and run animation
  useEffect(() => {
    const gl = glRef.current
    const program = programRef.current
    const canvas = canvasRef.current

    if (!gl || !program || !canvas) return

    gl.useProgram(program)

    // Get uniform locations
    const uniforms = {
      time: gl.getUniformLocation(program, 'u_time'),
      resolution: gl.getUniformLocation(program, 'u_resolution'),
      groundColor: gl.getUniformLocation(program, 'u_groundColor'),
      topColor1: gl.getUniformLocation(program, 'u_topColor1'),
      topColor2: gl.getUniformLocation(program, 'u_topColor2'),
      topColor3: gl.getUniformLocation(program, 'u_topColor3'),
      bottomColor1: gl.getUniformLocation(program, 'u_bottomColor1'),
      bottomColor2: gl.getUniformLocation(program, 'u_bottomColor2'),
      hazeColor: gl.getUniformLocation(program, 'u_hazeColor'),
    }

    // Set color uniforms from palette
    const p = palette.hero
    gl.uniform3fv(uniforms.groundColor, hexToRgb(p.ground))
    gl.uniform3fv(uniforms.topColor1, hexToRgb(p.topField[0]))
    gl.uniform3fv(uniforms.topColor2, hexToRgb(p.topField[1]))
    gl.uniform3fv(uniforms.topColor3, hexToRgb(p.topField[2] || p.topField[1]))
    gl.uniform3fv(uniforms.bottomColor1, hexToRgb(p.bottomField[0]))
    gl.uniform3fv(uniforms.bottomColor2, hexToRgb(p.bottomField[1] || p.bottomField[0]))
    gl.uniform3fv(uniforms.hazeColor, hexToRgb(p.haze[0]))

    // Animation loop
    const render = () => {
      const time = (Date.now() - startTimeRef.current) / 1000

      gl.uniform1f(uniforms.time, time)
      gl.uniform2f(uniforms.resolution, canvas.width, canvas.height)

      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)

      animationRef.current = requestAnimationFrame(render)
    }

    render()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [palette])

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 w-full h-full ${className}`}
      style={{ display: 'block' }}
    />
  )
}
